import { Injectable } from '@angular/core';
import { StorageService } from '../storageService/storage.service';
import { FormGroup } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { DateTimePickerValueType } from '@nagel-types';
import { DATE_TIME_DEFAULT_VALUES } from '@nagel-consts';
import { convertDateTimePickerValueToDate, getEndOfDay, getParsedDateTimePickerDates, getStartOfDay } from '@nagel-utils';

@Injectable({
    providedIn: 'root'
})
export class DateTimeRangeService {
    form!: FormGroup;

    currentDateTimeSubject: BehaviorSubject<DateTimePickerValueType> = new BehaviorSubject<DateTimePickerValueType>(DATE_TIME_DEFAULT_VALUES);
    currentDateTime$ = this.currentDateTimeSubject.asObservable();
    isMenuElement = false;

    constructor(
        private storageService: StorageService
    ) { }

    private getShouldExecuteValidation = () => {
        return !this.isMenuElement;
    };

    private getDateTimePickerProcessedValue = (value: DateTimePickerValueType): DateTimePickerValueType => {
        const result = structuredClone(value);

        if (result.date && !result?.date?.startDate) {
            result.date.startDate = getStartOfDay(new Date());
            result.date.endDate =  getEndOfDay(new Date());
        }

        return result;
    };

    private getInitialData = (storageKey: string, value?: DateTimePickerValueType | null) => {
        return value
            ? this.getDateTimePickerProcessedValue(value)
            : this.getLocalStorageParsedData(storageKey) || DATE_TIME_DEFAULT_VALUES;
    };

    handleOnInit = (storageKey: string, isMenuElement: boolean = false, value?: DateTimePickerValueType | null) => {
        const dateTimePickerValue = this.getInitialData(storageKey, value);
        this.isMenuElement = isMenuElement;

        if (dateTimePickerValue) {
            const { date, time } = dateTimePickerValue;
            const { startTime, endTime } = time || { hours: '00', minutes: '00' };
            const dateFormGroup = this.getDateFormGroup();
            const timeFormGroup = this.getTimeFormGroup();

            const parsedStartDate = convertDateTimePickerValueToDate(date?.startDate, startTime);
            const parsedEndDate = convertDateTimePickerValueToDate(date?.endDate, endTime);

            dateFormGroup.patchValue({
                startDate: parsedStartDate,
                endDate: parsedEndDate
            });
            timeFormGroup.patchValue({
                startTime: {
                    hours: startTime?.hours,
                    minutes: startTime?.minutes
                },
                endTime: {
                    hours: endTime?.hours,
                    minutes: endTime?.minutes
                }
            });

            this.currentDateTimeSubject.next({
                date: {
                    startDate: date?.startDate,
                    endDate: date?.endDate
                },
                time: {
                    startTime: {
                        hours: startTime?.hours,
                        minutes: startTime?.minutes
                    },
                    endTime: {
                        hours: endTime?.hours,
                        minutes: endTime?.minutes
                    }
                }
            });
        }
    };

    saveToLocalStorage = (storageKey: string) => {
        const formData = this.form && this.form.getRawValue();

        if (formData) {
            const parsedData = JSON.stringify(formData);

            if (!this.form.invalid) {
                this.storageService.saveItem(storageKey, parsedData);
            }
        }
    };

    setForm = (form: FormGroup) => {
        this.form = form;
    };

    getForm = () => {
        return this.form;
    };

    setErrors = (error: object | null) => {
        if (this.getShouldExecuteValidation()) {
            this.getForm().get('date')?.setErrors(error);
        }
    };

    getLocalStorageParsedData = (storageKey: string) => {
        const storageData = this.storageService.getItem(storageKey);

        return storageData ? JSON.parse(storageData) : null;
    };

    patchFormValues = (formValues: DateTimePickerValueType) => {
        const  { startDate, endDate } = getParsedDateTimePickerDates(formValues);

        this.form.patchValue({
            date: {
                startDate,
                endDate
            }
        });
    };

    getDateFormGroup = () => {
        return this.form.get('date') as FormGroup;
    };

    getTimeFormGroup = () => {
        return this.form.get('time') as FormGroup;
    };

    getStartTimeFormGroup = () => {
        return this.form.get('time.startTime') as FormGroup;
    };

    getEndTimeFormGroup = () => {
        return this.form.get('time.endTime') as FormGroup;
    };

}